import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Calendar, Award, User, Sparkles, GraduationCap, BookOpen } from 'lucide-react';
import { Badge } from './ui/badge';
import { getLocalizedText, formatPrice, formatDate } from '../utils/helpers';

export function RatingStars({ rating, count }) {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= Math.round(rating) ? 'fill-gold text-gold' : 'text-gray-300'}`}
          style={star <= Math.round(rating) ? { fill: '#D4A574', color: '#D4A574' } : {}}
        />
      ))}
      {count !== undefined && <span className="text-sm text-secondary ml-1">({count})</span>}
    </div>
  );
}

export function ServiceCard({ service, language = 'es' }) {
  const hasImage = service.images && service.images.length > 0;
  return (
    <Link to={`/service/${service.id}`} data-testid={`service-card-${service.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="h-48 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img src={service.images[0]} alt={getLocalizedText(service.title, language)} className="w-full h-full object-cover" />
          ) : (
            <Sparkles className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
        </div>
        <div className="p-5">
          {service.is_featured && <Badge className="mb-2 bg-gold text-white" style={{ backgroundColor: '#D4A574' }}>Destacado</Badge>}
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{getLocalizedText(service.title, language)}</h3>
          <p className="text-sm text-secondary mb-2">{service.teacher_name}</p>
          <div className="flex items-center text-sm text-secondary mb-2">
            <MapPin className="h-4 w-4 mr-1" />
            <span>{service.location?.city || 'Online'}</span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="font-semibold text-gold" style={{ color: '#D4A574' }}>{formatPrice(service.price, service.currency)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function CourseCard({ course, language = 'es' }) {
  const schoolName = course.school_name ? getLocalizedText(course.school_name, language) : course.teacher_name;
  const hasImage = course.images && course.images.length > 0;
  return (
    <Link to={`/course/${course.id}`} data-testid={`course-card-${course.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="h-48 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img src={course.images[0]} alt={getLocalizedText(course.title, language)} className="w-full h-full object-cover" />
          ) : (
            <GraduationCap className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            {course.is_featured && <Badge className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }}>Destacado</Badge>}
            {course.is_online && <Badge variant="outline">Online</Badge>}
          </div>
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{getLocalizedText(course.title, language)}</h3>
          <p className="text-sm text-secondary mb-2">{schoolName}</p>
          <div className="flex items-center text-sm text-secondary mb-2">
            <Calendar className="h-4 w-4 mr-1" />
            <span>{course.start_date ? formatDate(course.start_date) : 'Fechas flexibles'}</span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="font-semibold text-gold" style={{ color: '#D4A574' }}>{formatPrice(course.price, course.currency)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function TeacherCard({ teacher, language = 'es' }) {
  const hasImage = teacher.images && teacher.images.length > 0;
  return (
    <Link to={`/teacher/${teacher.id}`} data-testid={`teacher-card-${teacher.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="h-48 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img src={teacher.images[0]} alt={teacher.user_name} className="w-full h-full object-cover" />
          ) : (
            <User className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{teacher.user_name}</h3>
          <p className="text-sm text-secondary mb-2 line-clamp-2">{getLocalizedText(teacher.bio, language)}</p>
          <div className="flex items-center text-sm text-secondary mb-2">
            <MapPin className="h-4 w-4 mr-1" />
            <span>{teacher.location?.city}</span>
          </div>
          <div className="flex items-center text-sm text-secondary mb-3">
            <Award className="h-4 w-4 mr-1" />
            <span>{teacher.experience_years} anos experiencia</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function SchoolCard({ school, language = 'es' }) {
  const hasImage = school.images && school.images.length > 0;
  return (
    <Link to={`/school/${school.id}`} data-testid={`school-card-${school.id}`}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover h-full">
        <div className="h-48 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
          {hasImage ? (
            <img src={school.images[0]} alt={getLocalizedText(school.name, language)} className="w-full h-full object-cover" />
          ) : (
            <BookOpen className="h-16 w-16 text-gold opacity-50" style={{ color: '#D4A574' }} />
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">{getLocalizedText(school.name, language)}</h3>
          <p className="text-sm text-secondary mb-2 line-clamp-2">{getLocalizedText(school.description, language)}</p>
          <div className="flex items-center text-sm text-secondary mb-3">
            <MapPin className="h-4 w-4 mr-1" />
            <span>{school.location?.city}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
